import React, { createContext, useContext, useRef, useEffect, useMemo, useCallback, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import {
  TIME_MODE_STORAGE_KEY,
  CROWD_DENSITY_STORAGE_KEY,
  CROWD_DENSITY_MIN,
  CROWD_DENSITY_MAX,
} from "./constants";

export const TimeOfDayContext = createContext(null);

export const useDayFactor = () => {
  const ctx = useContext(TimeOfDayContext);
  return ctx ? ctx.dayFactorRef : { current: 0 };
};

// ---------------------------------------------------------------------------
// Crowd density — manual override of NPC count.
//   densityRef.current : null  → auto (day/night drives count)
//                        number → exact requested count (clamped)
// ---------------------------------------------------------------------------
export const CrowdContext = createContext(null);

export const useCrowdDensity = () => {
  const ctx = useContext(CrowdContext);
  return ctx ? ctx.densityRef : { current: null };
};

export function useCrowdState() {
  const readInitial = () => {
    if (typeof window === "undefined") return null;
    try {
      const stored = window.localStorage.getItem(CROWD_DENSITY_STORAGE_KEY);
      if (stored === null || stored === "auto") return null;
      const n = Number(stored);
      if (!Number.isFinite(n)) return null;
      return Math.max(CROWD_DENSITY_MIN, Math.min(CROWD_DENSITY_MAX, Math.round(n)));
    } catch (e) {
      return null;
    }
  };

  const [density, setDensityState] = useState(readInitial);
  const densityRef = useRef(density);
  useEffect(() => {
    densityRef.current = density;
  }, [density]);

  const setDensity = useCallback((v) => {
    if (v === null || v === "auto") {
      setDensityState(null);
      try {
        window.localStorage.removeItem(CROWD_DENSITY_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
      return;
    }
    const n = Math.max(CROWD_DENSITY_MIN, Math.min(CROWD_DENSITY_MAX, Math.round(v)));
    setDensityState(n);
    try {
      window.localStorage.setItem(CROWD_DENSITY_STORAGE_KEY, String(n));
    } catch (e) {
      /* ignore */
    }
  }, []);

  const contextValue = useMemo(() => ({ densityRef }), [densityRef]);
  return { density, setDensity, contextValue };
}

// Day: 7–17 → 1.0. Night: 19–5 → 0.0. Dawn 5–7 ramp 0→1. Dusk 17–19 ramp 1→0.
export function computeAutoDayFactor(date = new Date()) {
  const h = date.getHours() + date.getMinutes() / 60;
  if (h >= 7 && h < 17) return 1;
  if (h >= 19 || h < 5) return 0;
  if (h >= 5 && h < 7) return (h - 5) / 2;
  return 1 - (h - 17) / 2;
}

// Smoothly drives dayFactorRef.current toward targetRef.current. Used inside Canvas.
export function TimeUpdater() {
  const ctx = useContext(TimeOfDayContext);
  useFrame((_, delta) => {
    if (!ctx) return;
    const cur = ctx.dayFactorRef.current;
    const tgt = ctx.targetRef.current;
    const speed = 0.7;
    ctx.dayFactorRef.current = cur + (tgt - cur) * Math.min(1, delta * speed);
  });
  return null;
}

// Color lerp helper — shared scratch colors live inside hook scope to avoid races.
export function makeColorLerper() {
  const a = new THREE.Color();
  const b = new THREE.Color();
  return function lerpHexInto(target, hexA, hexB, t) {
    a.set(hexA);
    b.set(hexB);
    target.copy(a).lerp(b, t);
  };
}

// React hook that owns time-of-day state + localStorage persistence + auto-detection.
// `urlOverride` (optional) — if provided, takes precedence over localStorage on
// the initial render (and is also persisted forward as the user's new choice).
export function useTimeOfDayState(initialMode = "auto", urlOverride = null) {
  const readInitial = () => {
    if (urlOverride === "auto" || urlOverride === "day" || urlOverride === "night") {
      return urlOverride;
    }
    if (typeof window === "undefined") return initialMode;
    try {
      const stored = window.localStorage.getItem(TIME_MODE_STORAGE_KEY);
      if (stored === "auto" || stored === "day" || stored === "night") return stored;
    } catch (e) {
      /* localStorage unavailable */
    }
    return initialMode;
  };

  const [timeMode, setTimeModeState] = useState(readInitial);

  // Persist URL-override on first mount so subsequent visits remember it.
  useEffect(() => {
    if (urlOverride === "auto" || urlOverride === "day" || urlOverride === "night") {
      try {
        window.localStorage.setItem(TIME_MODE_STORAGE_KEY, urlOverride);
      } catch (e) {
        /* ignore */
      }
    }
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialFactor =
    timeMode === "day" ? 1 : timeMode === "night" ? 0 : computeAutoDayFactor();

  const dayFactorRef = useRef(initialFactor);
  const targetRef = useRef(initialFactor);
  const [autoLabel, setAutoLabel] = useState(() =>
    computeAutoDayFactor() > 0.5 ? "Day" : "Night"
  );

  useEffect(() => {
    const compute = () => {
      if (timeMode === "day") {
        targetRef.current = 1;
      } else if (timeMode === "night") {
        targetRef.current = 0;
      } else {
        const t = computeAutoDayFactor();
        targetRef.current = t;
        setAutoLabel(t > 0.5 ? "Day" : "Night");
      }
    };
    compute();
    const id = setInterval(compute, 30000);
    return () => clearInterval(id);
  }, [timeMode]);

  const setTimeMode = useCallback((m) => {
    setTimeModeState(m);
    try {
      window.localStorage.setItem(TIME_MODE_STORAGE_KEY, m);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const contextValue = useMemo(
    () => ({ dayFactorRef, targetRef }),
    [dayFactorRef, targetRef]
  );

  return { timeMode, setTimeMode, autoLabel, contextValue, dayFactorRef };
}
